import React from 'react';
import { Typography, Chip, Box } from '@mui/material';
import { format, parseISO } from 'date-fns';
import { TableActionButton, TableActionGroup } from './TableActions';

/**
 * Utility to get nested field value from an object
 * @param {Object} obj - The object to get value from
 * @param {string} path - Dot-notation path (e.g., "user.name")
 */
const getNestedValue = (obj, path) => {
    if (!path) return obj;
    return path.split('.').reduce((current, key) => current?.[key], obj);
};

/**
 * Evaluates a condition against a row and context
 * @param {Object} condition - The condition to evaluate
 * @param {Object} row - The row data
 * @param {Object} context - Additional context (e.g., selectedCompanyId)
 */
const evaluateCondition = (condition, row, context = {}) => {
    if (!condition) return true;

    const { field, operator, value, contextField, conditions } = condition;

    // Compound conditions: and / or
    if (operator === 'and' && Array.isArray(conditions)) {
        return conditions.every(c => evaluateCondition(c, row, context));
    }
    if (operator === 'or' && Array.isArray(conditions)) {
        return conditions.some(c => evaluateCondition(c, row, context));
    }

    const fieldValue = getNestedValue(row, field);

    switch (operator) {
        case 'equals':
            // Use == for loose comparison to handle type differences
            // eslint-disable-next-line eqeqeq
            return fieldValue == value;
        case 'notEquals':
            // eslint-disable-next-line eqeqeq
            return fieldValue != value;
        case 'contains':
            return String(fieldValue).includes(value);
        case 'equalsContext':
            // Use == for loose comparison to handle string/number differences
            // eslint-disable-next-line eqeqeq
            return fieldValue == context[contextField];
        case 'notEqualsContext':
            // eslint-disable-next-line eqeqeq
            return fieldValue != context[contextField];
        case 'exists':
            return fieldValue != null && fieldValue !== '';
        case 'notExists':
            return fieldValue == null || fieldValue === '';
        case 'in':
            return Array.isArray(value) && value.includes(fieldValue);
        case 'notIn':
            return Array.isArray(value) && !value.includes(fieldValue);
        case 'existsInContextArray': {
            // Check if fieldValue exists in a context array where a property matches
            // Used for: checking if row.key exists in userWorkflowActions where actionId === 'cloneWorkflow'
            const { matchField, filterField, filterValue } = condition;
            const contextArray = context[contextField];
            if (!Array.isArray(contextArray)) return false;
            return contextArray.some(item =>
                item[matchField] === fieldValue &&
                (!filterField || item[filterField] === filterValue)
            );
        }
        case 'hasWorkflowPermission':
            // Config names only the permission code ({ permission: "clone_workflow", field:
            // "processDefinitionKey" }); how it's checked is the page's context.canUse
            // (see useWorkflowAccess), so config never depends on page internals.
            return typeof context.canUse === 'function' && context.canUse(condition.permission, fieldValue);
        case 'contextEquals':
            // Check if a context value equals the specified value (no row field needed)
            // eslint-disable-next-line eqeqeq
            return context[contextField] == value;
        case 'contextNotEquals':
            // eslint-disable-next-line eqeqeq
            return context[contextField] != value;
        default:
            return true;
    }
};

/**
 * Text Renderer - renders a Typography component
 */
const TextRenderer = ({ row, config, t }) => {
    const { fieldPath, fallbackFieldPaths, variant = 'body1', translateValue = false, sx = {} } = config;
    let value = getNestedValue(row, fieldPath);

    if ((value === null || value === undefined || value === '') && Array.isArray(fallbackFieldPaths)) {
        for (const path of fallbackFieldPaths) {
            const fallbackValue = getNestedValue(row, path);
            if (fallbackValue !== null && fallbackValue !== undefined && fallbackValue !== '') {
                value = fallbackValue;
                break;
            }
        }
    }

    value = value ?? '';
    const displayValue = translateValue ? t(value) : value;

    return (
        <Typography variant={variant} sx={sx}>
            {displayValue}
        </Typography>
    );
};

/**
 * Chip Renderer - renders a MUI Chip component
 */
const ChipRenderer = ({ row, config, t }) => {
    const { fieldPath, colorMapping = {}, size = 'small', variant = 'filled', translateValue = true } = config;
    const value = getNestedValue(row, fieldPath) ?? '';
    const color = colorMapping[value] || 'default';
    const label = translateValue ? t(value) : value;

    return (
        <Chip
            label={label}
            color={color}
            size={size}
            variant={variant}
        />
    );
};

/**
 * Date Renderer - renders a formatted date
 */
const DateRenderer = ({ row, config, t }) => {
    const { fieldPath, format: dateFormat = 'dd/MM/yyyy', variant = 'body1' } = config;
    const value = getNestedValue(row, fieldPath);

    if (!value) return <Typography variant={variant}>-</Typography>;

    try {
        const date = typeof value === 'string' ? parseISO(value) : value;
        const formatted = format(date, dateFormat);
        return <Typography variant={variant}>{formatted}</Typography>;
    } catch {
        return <Typography variant={variant}>{t(value)}</Typography>;
    }
};

/**
 * Actions Renderer - renders action buttons
 */
const ActionsRenderer = ({ row, rowIndex, config, actions = {}, context = {}, t }) => {
    const { buttons = [] } = config;

    return (
        <TableActionGroup>
            {buttons.map((buttonConfig) => {
                const {
                    id,
                    icon,
                    label,
                    tooltip,
                    color = 'primary',
                    actionKey,
                    condition,
                    hideCondition,
                    variant,
                    text,
                    size,
                    disabled,
                } = buttonConfig;

                // Show condition: hide if condition evaluates to false
                if (condition && !evaluateCondition(condition, row, context)) {
                    return null;
                }

                // Hide condition: hide if hideCondition evaluates to true
                if (hideCondition && evaluateCondition(hideCondition, row, context)) {
                    return null;
                }

                const onClick = actions[actionKey];
                if (!onClick) {
                    console.warn(`Action "${actionKey}" not provided for button "${id}"`);
                    return null;
                }

                return (
                    <TableActionButton
                        key={id}
                        icon={icon}
                        label={label}
                        tooltip={tooltip}
                        color={color}
                        text={text}
                        variant={variant}
                        size={size}
                        disabled={disabled}
                        onClick={() => onClick(row, rowIndex)}
                    />
                );
            })}
        </TableActionGroup>
    );
};

/**
 * Ribbon Renderer - renders a corner ribbon with overflow hidden container
 */
const RibbonRenderer = ({ config, theme }) => {
    const { text = 'SELECTED', backgroundColor = '#2e3b7d' } = config;
    const isRTL = theme?.direction === 'rtl';

    return (
        <Box
            sx={{
                position: "absolute",
                top: 0,
                right: isRTL ? 'auto' : 0,
                left: isRTL ? 0 : 'auto',
                width: "85px",
                height: "85px",
                overflow: "hidden",
                pointerEvents: "none",
                zIndex: 1,
                "&::before": {
                    content: `"${text}"`,
                    position: "absolute",
                    top: "15px",
                    right: isRTL ? "auto" : "-22px",
                    left: isRTL ? "-22px" : "auto",
                    width: "100px",
                    background: backgroundColor,
                    color: "white",
                    textAlign: "center",
                    py: "3px",
                    fontSize: "10px",
                    fontWeight: "bold",
                    textTransform: "uppercase",
                    transform: isRTL ? "rotate(-45deg)" : "rotate(45deg)",
                }
            }}
        />
    );
};

/**
 * Conditional Renderer - renders based on condition evaluation
 */
const ConditionalRenderer = ({ row, rowIndex, config, actions, context, t, theme }) => {
    const { condition, trueRender, falseRender } = config;
    const shouldRenderTrue = evaluateCondition(condition, row, context);
    const renderConfig = shouldRenderTrue ? trueRender : falseRender;

    if (!renderConfig) return null;

    return createRenderer(renderConfig.renderType)({
        row,
        rowIndex,
        config: renderConfig.renderConfig,
        actions,
        context,
        t,
        theme
    });
};

// Renderer Registry
const RENDERERS = {
    text: TextRenderer,
    chip: ChipRenderer,
    date: DateRenderer,
    actions: ActionsRenderer,
    ribbon: RibbonRenderer,
    conditional: ConditionalRenderer,
};

/**
 * Factory function to get a renderer by type
 * @param {string} renderType - The type of renderer
 */
export const createRenderer = (renderType) => {
    return RENDERERS[renderType] || TextRenderer;
};

/**
 * Main function to build a render function from column config
 * @param {Object} columnConfig - The column configuration from database
 * @param {Object} options - Options including actions, context, t (translation), theme
 * @returns {Function} A render function compatible with DataTable
 */
export const buildColumnRenderFunction = (columnConfig, options = {}) => {
    const { actions = {}, context = {}, t = (v) => v, theme } = options;
    const { renderType, renderConfig, key } = columnConfig;

    // If no renderType specified, use default text renderer with key as fieldPath
    if (!renderType) {
        return (row) => {
            const value = getNestedValue(row, key) ?? '';
            return <Typography variant="body1">{t(value)}</Typography>;
        };
    }

    const Renderer = createRenderer(renderType);

    return (row, rowIndex) => (
        <Renderer
            row={row}
            rowIndex={rowIndex}
            config={renderConfig || { fieldPath: key }}
            actions={actions}
            context={context}
            t={t}
            theme={theme}
        />
    );
};

// Export utilities for external use
export { getNestedValue, evaluateCondition };
